import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { ThemeContext, type Theme, type ThemeContextType } from '../hooks/useTheme';

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user, customer, isAuthenticated } = useAuth();
  const [theme, setThemeState] = useState<Theme>('system');
  const [actualTheme, setActualTheme] = useState<'light' | 'dark'>('light');

  // Function to get current organization ID from portal context
  const getCurrentOrganizationId = (): string | null => {
    // Try to get organization ID from global portal context
    const portalContext = window.__PORTAL_CONTEXT__;
    if (portalContext?.organizationInfo?.organizationId) {
      return portalContext.organizationInfo.organizationId;
    }
    
    // Fallback: extract from URL path
    const pathMatch = window.location.pathname.match(/^\/org\/([^/]+)/);
    if (pathMatch) {
      // We have the slug, but we need the organizationId. For now, return null
      // and let the calling code handle this case
      return null;
    }
    
    return null;
  };

  // Function to determine current portal context and get appropriate theme key
  const getThemeStorageKey = useCallback((): string | null => {
    const isCustomerPortalRoute = window.location.pathname.includes('/portal');
    
    if (isCustomerPortalRoute) {
      const organizationId = getCurrentOrganizationId();
      if (organizationId) {
        return `customerTheme_${organizationId}`;
      }
      // Return null if we can't determine organization - don't persist theme
      return null;
    }
    
    return 'adminTheme';
  }, []);

  // Function to get the system theme preference
  const getSystemTheme = (): 'light' | 'dark' => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };

  // Function to compute the actual theme based on current theme setting
  const computeActualTheme = useCallback((currentTheme: Theme): 'light' | 'dark' => {
    if (currentTheme === 'system') {
      return getSystemTheme();
    }
    return currentTheme;
  }, []);

  // Function to apply theme to document
  const applyTheme = (themeToApply: 'light' | 'dark') => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(themeToApply);
    setActualTheme(themeToApply);
  };

  // Initialize theme from user/customer profile or localStorage
  useEffect(() => {
    const isCustomerPortal = window.location.pathname.includes('/portal');
    const themeKey = getThemeStorageKey();
    let themeToApply: Theme = 'system';
    
    if (isAuthenticated) {
      // Use theme from authenticated user/customer profile
      if (isCustomerPortal && customer?.theme) {
        themeToApply = customer.theme as Theme;
      } else if (!isCustomerPortal && user?.theme) {
        themeToApply = user.theme as Theme;
      } else if (themeKey) {
        // Fallback to localStorage for the appropriate portal (only if we have a valid key)
        let savedTheme = localStorage.getItem(themeKey) as Theme;
        
        // Migration logic for customer portals: check for legacy customerTheme
        if (isCustomerPortal && !savedTheme) {
          const legacyTheme = localStorage.getItem('customerTheme') as Theme;
          if (legacyTheme) {
            // Migrate legacy theme to organization-specific key
            localStorage.setItem(themeKey, legacyTheme);
            savedTheme = legacyTheme;
            // Legacy theme will be cleaned up by cleanup functions
          }
        }
        
        themeToApply = savedTheme || 'system';
      }
      // If themeKey is null (can't determine organization), just use 'system' default
    } else {
      // Not authenticated - use localStorage for the appropriate portal (only if we have a valid key)
      if (themeKey) {
        let savedTheme = localStorage.getItem(themeKey) as Theme;
        
        // Migration logic for customer portals: check for legacy customerTheme
        if (isCustomerPortal && !savedTheme) {
          const legacyTheme = localStorage.getItem('customerTheme') as Theme;
          if (legacyTheme) {
            // Migrate legacy theme to organization-specific key
            localStorage.setItem(themeKey, legacyTheme);
            savedTheme = legacyTheme;
            // Legacy theme will be cleaned up by cleanup functions
          }
        }
        
        themeToApply = savedTheme || 'system';
      }
      // If themeKey is null (can't determine organization), just use 'system' default
    }

    // Only save to localStorage if we have a valid storage key
    if (themeKey) {
      localStorage.setItem(themeKey, themeToApply);
    }
    
    setThemeState(themeToApply);
    const computed = computeActualTheme(themeToApply);
    applyTheme(computed);
  }, [isAuthenticated, user?.theme, customer?.theme, computeActualTheme, getThemeStorageKey]);

  // Listen for system theme changes when theme is set to 'system'
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = () => {
      if (theme === 'system') {
        const computed = computeActualTheme('system');
        applyTheme(computed);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, computeActualTheme]);

  // Function to set theme and persist to backend if authenticated
  const setTheme = async (newTheme: Theme) => {
    setThemeState(newTheme);
    const computed = computeActualTheme(newTheme);
    applyTheme(computed);

    // Save to appropriate localStorage key (only if we have a valid key)
    const themeKey = getThemeStorageKey();
    if (themeKey) {
      localStorage.setItem(themeKey, newTheme);
    }

    // If user is authenticated, save to backend
    const isCustomerPortal = window.location.pathname.includes('/portal');
    
    if (isAuthenticated) {
      try {
        if (isCustomerPortal && customer) {
          // Save customer theme to backend
          await apiService.updateCustomerProfile({
            firstName: customer.firstName,
            lastName: customer.lastName,
            theme: newTheme
          });
        } else if (!isCustomerPortal && user) {
          // Save admin user theme to backend
          await apiService.updateProfile({
            firstName: user.firstName,
            lastName: user.lastName,
            theme: newTheme
          });
        }
      } catch (error) {
        console.error('Failed to save theme preference:', error);
        // Could show a toast notification here
      }
    }
  };

  const value: ThemeContextType = {
    theme,
    setTheme,
    actualTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};